/**
 * InquiriesPage.jsx — triage inbox for web leads from rotoflightmedia.com
 * (route /jobs/inquiries, lives under Jobs in the sidebar).
 *
 * Shows every job the marketing site created (source 'web-order' from the
 * /real-estate/order checkout, 'web-inquiry' from the video/photo/TV inquiry
 * form), split into three tabs:
 *
 *   New       — status 'intake', never triaged. These drive the sidebar badge.
 *   Handled   — qualified from here, or progressed in the pipeline by any path.
 *   Dismissed — voided from here (spam/junk); restorable.
 *
 * Actions write through the normal jobs PUT (useUpdateJob), so realtime
 * invalidation keeps this list, the pipeline, and the badge in sync:
 *   Qualify → stamps triagedAt, jumps to the job in the pipeline.
 *   Dismiss → status 'void' (+ void_reason), stamps triagedAt.
 *   Restore → status back to 'intake', triagedAt cleared (re-enters New).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, IconButton, Tabs, Tab, Chip, Stack, Button,
  Collapse, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Divider, Link as MuiLink, Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import PhoneIcon from '@mui/icons-material/PhoneOutlined';
import EventIcon from '@mui/icons-material/EventOutlined';
import InboxIcon from '@mui/icons-material/MoveToInboxOutlined';
import { useJobs, useUpdateJob } from '../hooks/useJobs';
import { useMobile } from '../hooks/useMobile';
import { fmtD, fmtC, STATUS_COLORS, formatStatus } from '../lib/utils';
import { partitionWebLeads, channelInfo, timeAgo, DISMISS_REASON } from '../lib/webLeads';

const EMPTY_COPY = {
  new: 'You’re all caught up. New web leads appear here the moment they land.',
  handled: 'Nothing handled yet. Qualified leads and progressed jobs show up here.',
  dismissed: 'Nothing dismissed. Junk or spam leads you dismiss land here and can be restored.',
};

function LeadCard({ job, tab, expanded, onToggle, onQualify, onDismiss, onRestore, busy }) {
  const channel = channelInfo(job);
  const isOrder = job.source === 'web-order';
  const shootDates = Array.isArray(job.shootDates) ? job.shootDates : [];
  const statusColor = STATUS_COLORS[job.status] || '#666';

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderLeft: `3px solid ${tab === 'new' ? channel.color : 'transparent'}`,
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' },
      }}
      onClick={onToggle}
    >
      {/* Top row: channel + title + meta */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap' }}>
            <Chip
              label={channel.label}
              size="small"
              sx={{ bgcolor: `${channel.color}22`, color: channel.color, fontWeight: 600, height: 20, fontSize: '0.65rem' }}
            />
            {tab !== 'new' && (
              <Chip
                label={formatStatus(job.status)}
                size="small"
                sx={{ bgcolor: `${statusColor}22`, color: statusColor, height: 20, fontSize: '0.65rem' }}
              />
            )}
            <Typography variant="caption" color="text.disabled">{timeAgo(job.createdAt)}</Typography>
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
            {job.title || job.clientName}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 0.5, flexWrap: 'wrap' }} alignItems="center">
            <Typography variant="caption" color="text.secondary">{job.clientName}</Typography>
            {job.contactEmail && (
              <MuiLink
                href={`mailto:${job.contactEmail}`}
                onClick={(e) => e.stopPropagation()}
                variant="caption"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
              >
                <EmailIcon sx={{ fontSize: 13 }} />{job.contactEmail}
              </MuiLink>
            )}
            {job.contactPhone && (
              <MuiLink
                href={`tel:${job.contactPhone}`}
                onClick={(e) => e.stopPropagation()}
                variant="caption"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}
              >
                <PhoneIcon sx={{ fontSize: 13 }} />{job.contactPhone}
              </MuiLink>
            )}
          </Stack>
        </Box>
        <Stack alignItems="flex-end" spacing={0.5}>
          {isOrder && job.revenue > 0 && (
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{fmtC(job.revenue)}</Typography>
          )}
          {isOrder && shootDates.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
              <EventIcon sx={{ fontSize: 13 }} />{fmtD(shootDates[0])}{shootDates.length > 1 ? ` +${shootDates.length - 1}` : ''}
            </Typography>
          )}
          <ExpandMoreIcon
            fontSize="small"
            sx={{
              color: 'text.disabled',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms',
            }}
          />
        </Stack>
      </Box>

      {/* Expanded detail: message / order lines. Clicks inside must not
          re-collapse the card (reading/selecting the message is normal). */}
      <Collapse in={expanded} unmountOnExit>
        <Box onClick={(e) => e.stopPropagation()} sx={{ cursor: 'auto' }}>
          <Divider sx={{ my: 1 }} />
          {shootDates.length > 1 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Preferred dates: {shootDates.map(fmtD).join(', ')}
            </Typography>
          )}
          {job.deliverables && (
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'text.secondary', m: 0, mb: job.notes ? 1 : 0 }}>
              {job.deliverables}
            </Typography>
          )}
          {job.notes && (
            <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', color: 'text.secondary', m: 0 }}>
              {job.notes}
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
        {tab === 'new' && (
          <>
            <Button size="small" variant="contained" disabled={busy} onClick={onQualify}>
              Qualify
            </Button>
            <Button size="small" color="error" disabled={busy} onClick={onDismiss}>
              Dismiss
            </Button>
          </>
        )}
        {tab === 'handled' && (
          <Button size="small" variant="outlined" onClick={onQualify}>
            Open in pipeline
          </Button>
        )}
        {tab === 'dismissed' && (
          <Button size="small" variant="outlined" disabled={busy} onClick={onRestore}>
            Restore
          </Button>
        )}
      </Stack>
    </Paper>
  );
}

export default function InquiriesPage() {
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const { data: allJobs = [], isLoading, isError, refetch } = useJobs();
  const updateJob = useUpdateJob();

  const [tab, setTab] = useState('new');
  const [expandedId, setExpandedId] = useState(null);
  const [confirmJob, setConfirmJob] = useState(null); // lead pending dismiss confirmation
  const [busyId, setBusyId] = useState(null);
  const [actionError, setActionError] = useState(null); // last failed triage action, human-readable

  const { needsTriage, handled, dismissed } = useMemo(() => partitionWebLeads(allJobs), [allJobs]);
  const lists = { new: needsTriage, handled, dismissed };
  const rows = lists[tab] || [];

  // Qualify: mark triaged (New tab only; Handled reuses this as plain open),
  // then work the lead in the normal pipeline. On failure, stay put and say so.
  const handleQualify = async (job) => {
    setActionError(null);
    if (!job.triagedAt && job.status === 'intake') {
      setBusyId(job.id);
      try {
        await updateJob.mutateAsync({ id: job.id, triagedAt: new Date().toISOString() });
      } catch (err) {
        setActionError(`Couldn’t qualify ${job.clientName || 'the lead'}: ${err.message || 'request failed'}. Nothing changed, try again.`);
        return;
      } finally {
        setBusyId(null);
      }
    }
    navigate(`/jobs?select=${job.id}`);
  };

  const handleDismiss = async (job) => {
    setActionError(null);
    setBusyId(job.id);
    try {
      await updateJob.mutateAsync({
        id: job.id,
        status: 'void',
        voidReason: DISMISS_REASON,
        triagedAt: new Date().toISOString(),
      });
      setConfirmJob(null); // only close on success; on failure the dialog stays for a retry
    } catch (err) {
      setActionError(`Couldn’t dismiss: ${err.message || 'request failed'}. Nothing changed, try again.`);
    } finally {
      setBusyId(null);
    }
  };

  // Restore clears the void residue explicitly: the server only auto-clears
  // void_reason/voided_at on a void → intake transition, and a dismissed lead
  // may have been auto-archived since (archived → intake skips that branch).
  const handleRestore = async (job) => {
    setActionError(null);
    setBusyId(job.id);
    try {
      await updateJob.mutateAsync({
        id: job.id,
        status: 'intake',
        triagedAt: null,
        voidReason: null,
        voidedAt: null,
        archivedFromVoid: false,
      });
    } catch (err) {
      setActionError(`Couldn’t restore: ${err.message || 'request failed'}. Nothing changed, try again.`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 860 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton size="small" onClick={() => navigate('/jobs')} sx={{ color: 'text.secondary' }}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>
          Inquiries
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1, display: { xs: 'none', sm: 'block' } }}>
          Web leads from rotoflightmedia.com
        </Typography>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_e, v) => { setTab(v); setExpandedId(null); }}
        variant={isMobile ? 'fullWidth' : 'standard'}
        sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5, fontSize: '0.8rem', textTransform: 'none' } }}
      >
        <Tab value="new" label={`New${needsTriage.length ? ` (${needsTriage.length})` : ''}`} />
        <Tab value="handled" label={`Handled${handled.length ? ` (${handled.length})` : ''}`} />
        <Tab value="dismissed" label={`Dismissed${dismissed.length ? ` (${dismissed.length})` : ''}`} />
      </Tabs>

      {/* Failed triage action — inline, dismissable, never silent */}
      {actionError && !confirmJob && (
        <Alert severity="error" onClose={() => setActionError(null)}>{actionError}</Alert>
      )}

      {/* List */}
      {isLoading ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          Loading…
        </Typography>
      ) : isError ? (
        <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Couldn’t load leads. The portal API may be restarting.
          </Typography>
          <Button size="small" variant="outlined" onClick={() => refetch()}>Retry</Button>
        </Paper>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ py: 6, px: 3, textAlign: 'center' }}>
          <InboxIcon sx={{ fontSize: 32, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body2" color="text.secondary">{EMPTY_COPY[tab]}</Typography>
        </Paper>
      ) : (
        <Stack spacing={1}>
          {rows.map((job) => (
            <LeadCard
              key={job.id}
              job={job}
              tab={tab}
              expanded={expandedId === job.id}
              onToggle={() => setExpandedId((cur) => (cur === job.id ? null : job.id))}
              onQualify={() => handleQualify(job)}
              onDismiss={() => setConfirmJob(job)}
              onRestore={() => handleRestore(job)}
              busy={busyId === job.id}
            />
          ))}
        </Stack>
      )}

      {/* Dismiss confirmation */}
      <Dialog open={!!confirmJob} onClose={() => setConfirmJob(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem' }}>Dismiss this lead?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            {confirmJob?.title || confirmJob?.clientName} moves to Dismissed and is voided in the
            pipeline. You can restore it later.
          </DialogContentText>
          {actionError && (
            <Alert severity="error" sx={{ mt: 1.5 }}>{actionError}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setConfirmJob(null)}>Cancel</Button>
          <Button
            size="small"
            color="error"
            variant="contained"
            disabled={!confirmJob || busyId === confirmJob.id}
            onClick={() => confirmJob && handleDismiss(confirmJob)}
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
